import clamp from "lodash/clamp";

export const COLOR_HEX_CODE_REGEXP = new RegExp("^#[0-9A-F]{6}$", "i");

export const isValidHexCode = (colorHexCode) => COLOR_HEX_CODE_REGEXP.test(colorHexCode);

export const isValidColor = (value) => {
  if (typeof window === "undefined" || typeof Option === "undefined") {
    return false;
  }
  const s = new Option().style;
  s.color = value;
  return s.color !== "";
};

export const sortInvalidColorLast = (a, b) => isValidHexCode(b?.name) - isValidHexCode(a?.name);

class Color {
  _luminance;
  _rgb;
  _hex;

  constructor(color) {
    switch (true) {
      case !color:
        this._rgb = [0, 0, 0];
        this._hex = "";
        break;
      case color.constructor?.name === Color.name:
        Object.assign(this, color.clone());
        break;
      case Array.isArray(color): {
        const clampedRgb = color.map((val) => clamp(val, 0, 255));

        this._rgb = clampedRgb;
        this._hex = Color.rgbToHexValue(clampedRgb);
        break;
      }
      case typeof color === "string":
        this._rgb = Color.hexToRgbValue(color);
        this._hex = color;
        break;
      default:
        throw Error("Invalid Color Specified");
    }
  }

  // * Private methods
  #calculateLuminance() {
    const rgb = this.rgb;

    const r = (rgb[0] / 255) * 0.299;
    const g = (rgb[1] / 255) * 0.587;
    const b = (rgb[2] / 255) * 0.114;

    return r + g + b;
  }

  // * Static methods
  static hexToRgbValue(hex) {
    const colorHexadecimal = hex.replace(/^#/, "");

    const bigint = parseInt(colorHexadecimal, 16);

    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return [r, g, b];
  }

  static rgbToHexValue(rgb) {
    return "#" + ((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1);
  }

  // * Getters
  get rgb() {
    return this._rgb;
  }

  get hex() {
    return this._hex;
  }

  get luminance() {
    this._luminance ||= this.#calculateLuminance();

    return this._luminance;
  }

  // * Public methods
  clone() {
    return new Color(this.rgb);
  }

  increaseBrightness(percentage = 5) {
    const rgb = this.rgb;

    const increasedRgb = rgb.map((component) =>
      Math.min(255, parseInt(component * (1 + percentage / 100))),
    );

    return new Color(increasedRgb);
  }

  toString() {
    return this.hex;
  }
}

export { Color };
